import { push } from '@lagunovsky/redux-react-router'
import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getStationArrowNav } from 'utils/ActionUtils'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import CatchmentPointsMap from '../CatchmentPointsMap'
import { Box } from '@mui/material'
import CatchmentAction from 'catchment/actions/CatchmentAction'
import DtoFile from 'components/file/dto/DtoFile'
import { DOCUMENT } from 'components/file/constant/FileConstants'
import StationAction from 'station/actions/StationAction'
import moment from 'moment'
import { getFileExtension, getFileName, getFormatedFileName } from 'utils/FileUtils'
import ToastrAction from 'toastr/actions/ToastrAction'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { isUndefined, max, isEmpty, compact, uniq, floor, values } from 'lodash'
import sandre from 'assets/pictures/logos/sandre.png'
import mtes from 'assets/pictures/logos/mtes.png'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import ProgressCard from 'components/card/ProgressCard'
import WatershedAction from 'referencial/components/watershed/actions/WatershedAction'

const CARTO_MNEMONIQUUE = 'CAR'
const IS_ACTIVE = '1'

const fillWatersheds = (obj, value) => {
    if (isUndefined(obj.watershed)) return { ...obj, watershed: value }
    else if (isUndefined(obj.watershed2)) return { ...obj, watershed2: value }
    else if (isUndefined(obj.watershed3)) return { ...obj, watershed3: value }
    else if (isUndefined(obj.watershed4)) return { ...obj, watershed4: value }
    return obj
}

const CatchmentMapApp = () => {
    const {
        catchment,
        catchments,
        files,
        watersheds,
        qualitometers,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        catchments: store.CatchmentReducer.catchments,
        files: store.StationReducer.files,
        watersheds: store.WatershedReducer.watersheds,
        qualitometers: store.QualityReducer.qualitometers,
    }), shallowEqual)
    const dispatch = useDispatch()

    const agencies = useSandreList(SANDRE.AGENCES)

    const [aacFile, setAacFile] = useState(undefined)
    const [watermassFile, setWatermassFile] = useState(undefined)
    const [loaded, setLoaded] = useState({
        aac: false,
        watermass: false,
        watersheds: false,
        stationWatersehd: false,
    })

    useEffect(() => {
        dispatch(WatershedAction.fetchWatersheds())
    }, [dispatch])

    const qualitometersLinked = useMemo(() => catchment.catchmentPoints
        ?.map(point => qualitometers.find(q => q.code === point.codeWithoutDesignation))
        .filter(q => !!q) || [],
    [catchment.catchmentPoints, qualitometers])

    const defaultSelectedPoint = useMemo(() => {
        const mainPointFound = catchment.catchmentPoints?.find(({ mainPoint }) => mainPoint)
        const code = mainPointFound?.codeWithoutDesignation
        const firstQualitometerLinked = qualitometersLinked?.[0]
        return qualitometers.find(q => q.code === code) || firstQualitometerLinked
    }, [catchment.catchmentPoints, qualitometers, qualitometersLinked])

    const handleRawDocuments = useCallback((document, referenceFiles) => {
        if (isUndefined(document?.name) || isUndefined(document?.url)) return {}
        const now = moment().valueOf()
        const formattedName = getFormatedFileName(catchment.code, IS_ACTIVE, getFileName(document.name), getFileExtension(document.name), CARTO_MNEMONIQUUE, now, STATION_TYPE_NAME.catchment)
        if (referenceFiles.some(f => f.name === formattedName)) {
            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
            return {}
        }
        const formattedFile = {
            url: document.url,
            name: formattedName,
            stationCode: catchment.code,
        }
        return new DtoFile(formattedFile, DOCUMENT)
    }, [catchment.code])

    const getAacKml = useCallback((specifiedFiles = undefined) => {
        const usedFiles = specifiedFiles ?? files
        const kmlAreaFiles = usedFiles.filter(f => f.type === CARTO_MNEMONIQUUE && f.name?.endsWith('.kml') && f.active === IS_ACTIVE).map(f => f.name)
        if (catchment.sandreCode && kmlAreaFiles.length === 0) {
            dispatch(CatchmentAction.fetchKML(`cdaac_${catchment.sandreCode}`))
                .then((document = undefined) => {
                    if (!isUndefined(document)) {
                        const formattedFile = handleRawDocuments(document, usedFiles)
                        setAacFile(formattedFile)
                    }
                }).finally(() => {
                    setLoaded(prev => ({ ...prev, aac: true }))
                })
        } else {
            setLoaded(prev => ({ ...prev, aac: true }))
        }
    }, [dispatch, catchment.sandreCode, handleRawDocuments])

    const getWatermassKml = useCallback(() => {
        dispatch(CatchmentAction.fetchWatermassKML(catchment.meCode?.replace('FR', '')))
            .then((document = undefined) => {
                if (!isUndefined(document)) {
                    const formattedFile = handleRawDocuments(document, files)
                    setWatermassFile({ ...formattedFile, watermassFromSandre: true })
                }
            }).finally(() => {
                setLoaded(prev => ({ ...prev, watermass: true }))
            })
    }, [dispatch, catchment.meCode, handleRawDocuments])

    const selectWatersheds = useCallback(() => {
        const qualitoWatersheds = compact(uniq(qualitometersLinked.flatMap(q => [q.watershed, q.watershed2, q.watershed3, q.watershed4])).map(id => watersheds.find(w => w.id === id)?.code ?? ''))
        const watershedsPromises = qualitoWatersheds.map(code => dispatch(CatchmentAction.fetchWatershedKML(code)))
        Promise.all(watershedsPromises)
            .then((documents = []) => {
                const watershedsIds = documents.reduce((acc, act) => fillWatersheds(acc, watersheds.find(w => w.code === act.name.split('.kml')[0])?.id), {})
                const watershedReducerPromises = WatershedAction.promiseStationWatersheds(watershedsIds).filter(v => v !== null)
                Promise.all(watershedReducerPromises).then(json => {
                    dispatch(WatershedAction.receiveStationWatersheds(json[0], json[1], json[2], json[3]))
                }).finally(() => {
                    setLoaded(prev => ({ ...prev, watersheds: true }))
                })
            }).catch(() => {
                setLoaded(prev => ({ ...prev, watersheds: true }))
            })
    }, [dispatch, qualitometersLinked, watersheds])

    const selectStationWatershed = useCallback(() => {
        if (isUndefined(defaultSelectedPoint?.x) || isUndefined(defaultSelectedPoint?.y)) {
            setLoaded(prev => ({ ...prev, stationWatersehd: true }))
            return
        }
        WatershedAction.promiseStationWatershed(defaultSelectedPoint.x, defaultSelectedPoint.y)
            .then(json => {
                dispatch(WatershedAction.receiveStationWatershed(json))
            }).finally(() => {
                setLoaded(prev => ({ ...prev, stationWatersehd: true }))
            })
    }, [dispatch, defaultSelectedPoint])

    useEffect(() => {
        dispatch(WatershedAction.resetWatershed())
        selectWatersheds()
        selectStationWatershed()
        getWatermassKml()
        if (!catchment.code || files.length > 0 && files[0].stationCode === catchment.code) {
            getAacKml()
            return
        }
        dispatch(StationAction.fetchFiles(catchment.code, STATION_TYPE_NAME.catchment))
            .then((documents = []) => {
                const formattedDocuments = documents.map(document => new DtoFile(document, DOCUMENT))
                getAacKml(formattedDocuments)
            })
    }, [dispatch, catchment.code, selectWatersheds, getAacKml, getWatermassKml, selectStationWatershed])

    const usedAacFile = useMemo(() => {
        if (!isEmpty(aacFile)) return { ...aacFile, aacFromSandre: true }
        if (files.length === 0) return undefined
        const kmlAreaFiles = files.filter(f => f.type === CARTO_MNEMONIQUUE && f.name?.endsWith('.kml') && f.active === IS_ACTIVE)
        const mostRecentFile = kmlAreaFiles.length > 0 ?
            kmlAreaFiles.reduce((mostRecent, act) => max([act.date, act.updateDate]) > max([mostRecent.date, mostRecent.updateDate]) ? act : mostRecent, kmlAreaFiles[0]) :
            undefined
        return mostRecentFile ? { ...mostRecentFile, aacFromSandre: false } : undefined
    }, [aacFile, files])

    useTitle(() => [{
        title: i18n.catchments,
        href: 'catchment',
    }, {
        title: catchment.code ? `[${catchment.code}] ${catchment.name ?? ''}` : catchment.name || i18n.unknown,
        href: `station/catchment/${catchment.id}`,
    }, {
        title: i18n.dashboard,
        href: `station/catchment/${catchment.id}`,
    }], [catchment])

    useActions(() => {
        const agency = agencies.find(a => a.code === catchment.pond)
        const ref = agency?.mnemonique || agency?.reference
        const mtesLink = {
            img: mtes,
            href: `http://www.deb.developpement-durable.gouv.fr/telechargements/fiche.php?IG=${ref}_${catchment.code}`,
            label: i18n.mtes,
        }
        const sandreLink = catchment.sandreCode ? {
            img: sandre,
            href: `https://id.eaufrance.fr/AAC/${catchment.sandreCode}`,
            label: i18n.sandre,
        } : undefined
        const links = compact([mtesLink, sandreLink])
        return {
            arrowNav: getStationArrowNav('catchment', catchments, catchment.id, s => dispatch(push(`/station/catchment/${s.id}`))),
            links,
        }
    }, [catchments, catchment])

    return (
        <Box sx={{ padding: 0, height: '100%' }}>
            {!values(loaded).some(e => e === false) && (
                <CatchmentPointsMap
                    catchmentPoints={catchment.catchmentPoints}
                    specificFiles={[usedAacFile, watermassFile]}
                />
            ) ||
            <ProgressCard progress={values(loaded).filter(l => l === true).length * floor(100 / values(loaded).length)}/>}
        </Box>
    )
}

export default CatchmentMapApp